/**
 * Free-text search across the three grids in this feature.
 *
 * Deliberately not the grid's built-in quick filter. The requirement is that search catches
 * any value a row carries *except* the count columns, and per-column opt-out differs between
 * MUI X versions. Filtering the rows ourselves also lets a search reach values that aren't
 * columns at all - the publisher names a line is active on, which is the thing you most want
 * to search for in the all-lines view and which no column exposes as text.
 *
 * Rows are already fully loaded client-side in all three views, so there's nothing to page.
 */

/** Everything about a row worth matching against, as strings. Nulls are dropped by the caller. */
export type Haystack = (string | number | null | undefined)[];

function normalise(value: string | number | null | undefined): string {
    return value === null || value === undefined ? '' : String(value).toLowerCase();
}

/**
 * All whitespace-separated terms must match, each anywhere in any field.
 *
 * AND rather than OR because that's what narrowing feels like: "amc direct" should mean the
 * DIRECT lines on AMC, not every row touching either word.
 */
export function matchesSearch(haystack: Haystack, query: string): boolean {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return true;

    const fields = haystack.map(normalise).filter(Boolean);
    if (!fields.length) return false;

    return terms.every((term) => fields.some((field) => field.includes(term)));
}

/** Filters rows by a per-row haystack. An empty query returns the rows untouched. */
export function searchRows<T>(rows: T[], query: string, haystackFor: (row: T) => Haystack): T[] {
    if (!query.trim()) return rows;
    return (rows || []).filter((row) => matchesSearch(haystackFor(row), query));
}
